import React from 'react';
import { Navigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Amplify } from 'aws-amplify';
import awsconfig from '../../src/aws-exports';
import AccessCardLoader from './admin/AccessCardSpinner'; // Import your AccessCardLoader

Amplify.configure(awsconfig);

const PrivateRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = React.useState(null);
  const [showLoader, setShowLoader] = React.useState(true);

  const handleLoaderComplete = () => {
    setShowLoader(false);
  };

  React.useEffect(() => {
    const checkAuth = async () => {
      console.log('=== PrivateRoute: Starting authentication check ===');
      
      // Check localStorage first
      const userFromStorage = localStorage.getItem('user');
      console.log('PrivateRoute: User from localStorage:', userFromStorage);
      
      try {
        const authSession = await fetchAuthSession();
        console.log('PrivateRoute: Auth session:', authSession);
        console.log('PrivateRoute: Auth session tokens:', authSession.tokens);
        
        // Check if the session is valid and has required tokens
        if (authSession && authSession.tokens && authSession.tokens.accessToken) {
          console.log('PrivateRoute: Valid tokens found, user is authenticated');
          
          // Wait a moment before setting authenticated to ensure loader animation can complete
          setTimeout(() => {
            setIsAuthenticated(true);
            
            // Ensure localStorage has user email if session is valid
            if (!userFromStorage && authSession.tokens.idToken?.payload?.email) {
              console.log('PrivateRoute: Setting user email in localStorage');
              localStorage.setItem('user', authSession.tokens.idToken.payload.email);
            }
          }, 1500); // Give the loader time to show meaningful progress
          
        } else {
          console.log('PrivateRoute: No valid tokens found');
          setTimeout(() => {
            setIsAuthenticated(false);
            // Clear localStorage if tokens are invalid
            if (userFromStorage) {
              console.log('PrivateRoute: Clearing invalid user from localStorage');
              localStorage.removeItem('user');
            }
          }, 2000); // Let the loader show failure state
        }
      } catch (error) {
        console.error('PrivateRoute: Authentication check failed:', error);
        console.log('PrivateRoute: Error details:', {
          message: error.message,
          name: error.name,
          stack: error.stack
        });
        
        setTimeout(() => {
          // Clear any stale user data from localStorage
          if (userFromStorage) {
            console.log('PrivateRoute: Clearing user from localStorage due to auth error');
            localStorage.removeItem('user');
          }
          
          setIsAuthenticated(false);
        }, 2000); // Let the loader show failure state
      }
      
      console.log('=== PrivateRoute: Authentication check complete ===');
    };

    checkAuth();
  }, []);

  // Show AccessCardLoader while checking authentication
  if (isAuthenticated === null) {
    console.log('PrivateRoute: Showing loading state');
    
    if (showLoader) {
      return (
        <div style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(241, 245, 249, 0.95)',
          backdropFilter: 'blur(10px)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <AccessCardLoader 
            onComplete={handleLoaderComplete}
            duration={2000} // 2 seconds for auth check
          />
        </div>
      );
    }
    
    // Fallback loading state if loader is complete but auth still pending
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        backgroundColor: 'rgba(248, 250, 252, 0.95)'
      }}>
        <div style={{ 
          fontSize: '18px', 
          color: '#475569',
          fontWeight: '500',
          marginBottom: '10px'
        }}>
          Finalizing authentication...
        </div>
        <div style={{ 
          fontSize: '14px', 
          color: '#64748b',
          opacity: 0.8,
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          Verifying your credentials and setting up your secure session
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (isAuthenticated === false) {
    console.log('PrivateRoute: User not authenticated, redirecting to /login');
    return <Navigate to="/login" replace />;
  }

  // Render children if authenticated
  console.log('PrivateRoute: User authenticated, rendering protected content');
  return children;
};

export default PrivateRoute;